import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

const ROWS = [
    {
        label: 'Tid per tilbud',
        today: "2–3 timer med regneark og manuelt oppsett i Word.",
        svenn: "15 minutter fra kalkyle til ferdig, nedlastet pristilbud."
    },
    {
        label: 'Marginen',
        today: "Viser seg først når jobben er ferdig og fakturert.",
        svenn: "Dekningsgrad og påslag ligger synlig i kalkylen før du sender."
    },
    {
        label: 'Pris',
        today: "Gratis i kroner, men timene og den tapte marginen koster.",
        svenn: "1 599 kr i måneden for hele bedriften, uansett antall brukere."
    }
]

/** Today versus Svenn: the old way gets struck through as you read down. */
export const CompareTable: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useScrollProgress(ref, { startAt: 0.8, endAt: 0.5 })

    return (
        <section id='sammenlign' className='tone-paper relative py-24 md:py-36' data-header='light'>
            <div className='wrap'>
                <div className='grid grid-cols-12 gap-x-6 gap-y-8'>
                    <div className='col-span-12 lg:col-span-7'>
                        <SectionLabel index='06'>Sammenlign</SectionLabel>
                        <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                            <SplitWords text='Svenn mot slik du gjør det i dag.' />
                        </h2>
                    </div>
                    <p className='col-span-12 self-end t-lead text-tone-muted pretty lg:col-span-4 lg:col-start-9'>
                        De fleste priser i Excel og Word, med erfaringstall i hodet. Det er der pengene forsvinner.
                    </p>
                </div>

                <div ref={ref} className='mt-16 md:mt-24' role='table' aria-label='Sammenligning av Excel, Word og erfaring mot Svenn Kalkulasjon'>
                    <div role='rowgroup'>
                        <div role='row' className='grid grid-cols-12 gap-x-6 border-b border-tone-line pb-4'>
                            <span role='columnheader' className='t-label col-span-12 hidden text-tone-muted md:col-span-3 md:block'>Hva</span>
                            <span role='columnheader' className='t-label col-span-6 text-tone-muted md:col-span-4'>Excel, Word og erfaring</span>
                            <span role='columnheader' className='t-label col-span-6 text-tone-ink md:col-span-5'>Med Svenn Kalkulasjon</span>
                        </div>
                    </div>

                    <div role='rowgroup'>
                        {ROWS.map((row, i) => (
                            <div
                                key={row.label}
                                role='row'
                                className='grid grid-cols-12 gap-x-6 gap-y-3 border-b border-tone-line py-8 md:py-10'
                                style={{ '--s': (i / ROWS.length).toFixed(3) } as CSSProperties}
                            >
                                <span role='rowheader' className='t-h3 col-span-12 md:col-span-3'>{row.label}</span>
                                <span role='cell' className='col-span-6 text-tone-muted md:col-span-4'>
                                    <span className='strike strike-thin text-lg leading-snug'>{row.today}</span>
                                </span>
                                <span role='cell' className='col-span-6 md:col-span-5'>
                                    <span className='block bg-night p-4 text-lg font-semibold leading-snug text-paper md:p-5'>{row.svenn}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
